/**
 * Test-fixture embryo report for the Embryo Testing deep links.
 *
 * What the Embryo Testing route shows is the state of the report on a cycle,
 * and the only cycles a capture account holds are whatever its clinic left
 * there, so a route pinned to one of them photographs whatever that record
 * happens to say today.
 *
 * Enabled with `?test=1&test_embryo_report=<key>` on a `__DEV__` build. The
 * fixture answers the read with the payload the endpoint itself returns:
 * `GET /cycles/{cycleId}/embryo-report` resolves to `data: null` for a cycle
 * whose report has not been filed, which is the empty state this catalog
 * captures and the state the entry form is opened from. The same key answers
 * the cryo vault read for the vault records the report's embryos link to.
 * Everything downstream is computed by product code from those payloads, on
 * the path a real record takes, so there is no second product behaviour to
 * maintain.
 */
import type { Specimen } from "@src/packages/care/cryovault/useSpecimens";
import type { Cycle, Phase } from "../hooks/useCycles";
import type {
  EmbryoReport,
  EmbryoReportHistoryEntry,
} from "../hooks/useEmbryoReport";
import { MOCK_CLOSED_CYCLE, MOCK_CYCLE_PHASES } from "./activeCycleFixtures";

type EmbryoResult = EmbryoReport["embryos"][number];

const REPORT_ID = "00000000-0000-0000-0000-0000000e0001";
const STAMP = "2026-08-14T09:00:00.000Z";

const AUDIT = {
  createdAt: STAMP,
  updatedAt: STAMP,
  updatedBy: null,
};

function euploidProfile(sex: "XX" | "XY"): EmbryoResult["cnProfile"] {
  const autosomes = Array.from({ length: 22 }, (_, index) => ({
    chr: String(index + 1),
    cn: 2,
    isAbnormal: false,
  }));
  return [
    ...autosomes,
    { chr: "X", cn: sex === "XX" ? 2 : 1, isAbnormal: false },
    { chr: "Y", cn: sex === "XX" ? 0 : 1, isAbnormal: false },
  ];
}

function withCopyNumber(
  profile: EmbryoResult["cnProfile"],
  chr: string,
  cn: number,
): EmbryoResult["cnProfile"] {
  return (profile ?? []).map((point) =>
    point.chr === chr ? { chr, cn, isAbnormal: true } : point,
  );
}

function embryo(
  embryoNumber: number,
  fields: Partial<EmbryoResult> &
    Pick<EmbryoResult, "karyotype" | "sex" | "result">,
): EmbryoResult {
  const id = `00000000-0000-0000-0000-0000000e010${embryoNumber}`;
  return {
    id,
    reportId: REPORT_ID,
    embryoBiopsyId: null,
    sampleId: `LAB-2408-0${embryoNumber}`,
    embryoNumber,
    grade: null,
    biopsyDay: 5,
    biopsyDate: "2026-08-02",
    transferRecommendation: "yes",
    affectedChromosomes: null,
    aneuploidyImpact: null,
    comments: null,
    genomicsProvider: null,
    cnProfile: null,
    mosaicism: null,
    embryoSpecimenId: null,
    monogenicFindings: [],
    polygenicRisks: [],
    developmentObservations: [],
    ...AUDIT,
    ...fields,
  };
}

function observation(
  embryoNumber: number,
  day: number,
  fields: Partial<EmbryoResult["developmentObservations"][number]>,
): EmbryoResult["developmentObservations"][number] {
  return {
    id: `00000000-0000-0000-0000-0000000e02${embryoNumber}${day}`,
    embryoSpecimenId: `00000000-0000-0000-0000-0000000e030${embryoNumber}`,
    day,
    pronucleiCount: null,
    polarBodyCount: null,
    gradingAlphabetical: null,
    gradingNumerical: null,
    cellCount: null,
    isArrested: null,
    stage: null,
    icmGrade: null,
    teGrade: null,
    notes: null,
    source: "provider",
    verificationStatus: "verified",
    verifiedAt: STAMP,
    verifiedBy: null,
    verificationNotes: null,
    ...AUDIT,
    ...fields,
  };
}

/**
 * A finalised whole-genome report with one embryo per result the sheet
 * distinguishes: a euploid embryo carrying every optional section, a mosaic
 * embryo, and an aneuploid embryo.
 */
const TESTED_REPORT: EmbryoReport = {
  id: REPORT_ID,
  patientId: "00000000-0000-0000-0000-0000000e0000",
  cycleId: "00000000-0000-0000-0000-00000000c002",
  treatmentPlanId: null,
  treatmentPlanVersion: null,
  reportState: "final",
  version: 1,
  supersedesReportId: null,
  correctionReason: null,
  reportDate: "2026-08-14",
  biopsyDate: "2026-08-02",
  retrievalDate: "2026-07-28",
  referringClinic: "Northlake Fertility",
  referringPhysician: "Dr. Amara Okafor",
  laboratory: "Orchid Health",
  indication: "Recurrent pregnancy loss",
  testType: "Whole Genome Sequencing",
  sampleType: "Trophectoderm biopsy",
  providerId: null,
  ownership: "patient",
  ownerId: "00000000-0000-0000-0000-0000000e0000",
  sourceId: null,
  audience: [],
  ...AUDIT,
  attachment: {
    id: "00000000-0000-0000-0000-0000000e0601",
    reportId: REPORT_ID,
    uploadId: "00000000-0000-0000-0000-0000000e0602",
    ...AUDIT,
    upload: {
      id: "00000000-0000-0000-0000-0000000e0602",
      name: "Orchid embryo report.pdf",
      contentDigest: null,
      url: null,
      uploadStatus: "uploaded",
      contentType: "application/pdf",
      size: null,
      notes: null,
      ownership: "patient",
      ownerId: "00000000-0000-0000-0000-0000000e0000",
      sourceId: null,
      audience: [],
      ...AUDIT,
    },
  },
  embryos: [
    embryo(1, {
      karyotype: "46,XX",
      sex: "XX",
      result: "euploid",
      grade: "4AA",
      genomicsProvider: "Orchid",
      cnProfile: euploidProfile("XX"),
      embryoSpecimenId: "00000000-0000-0000-0000-0000000e0301",
      comments: "Expanded blastocyst with a tightly packed inner cell mass.",
      developmentObservations: [
        observation(1, 1, { pronucleiCount: "2PN", polarBodyCount: "2PB" }),
        observation(1, 3, { cellCount: 8, gradingAlphabetical: "A" }),
        observation(1, 5, {
          stage: "expanded_blastocyst",
          gradingNumerical: 4,
          icmGrade: "A",
          teGrade: "A",
        }),
      ],
      monogenicFindings: [
        {
          id: "00000000-0000-0000-0000-0000000e0401",
          genomicResultId: "00000000-0000-0000-0000-0000000e0101",
          condition: "Cystic fibrosis",
          gene: "CFTR",
          status: "carrier",
          inheritancePattern: "Autosomal recessive",
          clinicalSignificance:
            "One copy of a pathogenic variant. Carriers are typically healthy.",
          ...AUDIT,
        },
        {
          id: "00000000-0000-0000-0000-0000000e0402",
          genomicResultId: "00000000-0000-0000-0000-0000000e0101",
          condition: "Spinal muscular atrophy",
          gene: "SMN1",
          status: "unaffected",
          inheritancePattern: "Autosomal recessive",
          clinicalSignificance: null,
          ...AUDIT,
        },
      ],
      polygenicRisks: [
        {
          id: "00000000-0000-0000-0000-0000000e0501",
          genomicResultId: "00000000-0000-0000-0000-0000000e0101",
          condition: "Type 2 diabetes",
          category: "Metabolic",
          percentile: 34,
          absoluteRisk: "8%",
          populationRisk: "11%",
          isElevated: false,
          ciLow: 22,
          ciHigh: 47,
          ...AUDIT,
        },
        {
          id: "00000000-0000-0000-0000-0000000e0502",
          genomicResultId: "00000000-0000-0000-0000-0000000e0101",
          condition: "Breast cancer",
          category: "Cancer",
          percentile: 81,
          absoluteRisk: "16%",
          populationRisk: "12%",
          isElevated: false,
          ciLow: 70,
          ciHigh: 90,
          ...AUDIT,
        },
      ],
    }),
    embryo(2, {
      karyotype: "46,XY,mos(7)",
      sex: "XY",
      result: "mosaic",
      grade: "4AB",
      biopsyDay: 6,
      transferRecommendation: "with_counseling",
      cnProfile: euploidProfile("XY"),
      mosaicism: [{ chromosome: "7", level: 35 }],
      embryoSpecimenId: "00000000-0000-0000-0000-0000000e0302",
      polygenicRisks: [
        {
          id: "00000000-0000-0000-0000-0000000e0503",
          genomicResultId: "00000000-0000-0000-0000-0000000e0102",
          condition: "Type 2 diabetes",
          category: "Metabolic",
          percentile: 58,
          absoluteRisk: "12%",
          populationRisk: "11%",
          isElevated: false,
          ciLow: 46,
          ciHigh: 68,
          ...AUDIT,
        },
        {
          id: "00000000-0000-0000-0000-0000000e0504",
          genomicResultId: "00000000-0000-0000-0000-0000000e0102",
          condition: "Breast cancer",
          category: "Cancer",
          percentile: 32,
          absoluteRisk: "9%",
          populationRisk: "12%",
          isElevated: false,
          ciLow: 21,
          ciHigh: 43,
          ...AUDIT,
        },
      ],
    }),
    embryo(3, {
      karyotype: "47,XY,+21",
      sex: "XY",
      result: "aneuploid",
      grade: "3BB",
      transferRecommendation: "no",
      cnProfile: withCopyNumber(euploidProfile("XY"), "21", 3),
      affectedChromosomes: ["21"],
      aneuploidyImpact:
        "An extra copy of chromosome 21. A pregnancy from this embryo would have trisomy 21.",
      polygenicRisks: [
        {
          id: "00000000-0000-0000-0000-0000000e0505",
          genomicResultId: "00000000-0000-0000-0000-0000000e0103",
          condition: "Type 2 diabetes",
          category: "Metabolic",
          percentile: 48,
          absoluteRisk: "10%",
          populationRisk: "11%",
          isElevated: false,
          ciLow: 37,
          ciHigh: 58,
          ...AUDIT,
        },
        {
          id: "00000000-0000-0000-0000-0000000e0506",
          genomicResultId: "00000000-0000-0000-0000-0000000e0103",
          condition: "Breast cancer",
          category: "Cancer",
          percentile: 72,
          absoluteRisk: "14%",
          populationRisk: "12%",
          isElevated: false,
          ciLow: 61,
          ciHigh: 82,
          ...AUDIT,
        },
      ],
    }),
  ],
};

const EARLIER_CYCLE_ID = "00000000-0000-0000-0000-00000000c001";

/** An older IVF retrieval whose embryos have no testing report yet. */
const UNREPORTED_CYCLE_ID = "00000000-0000-0000-0000-00000000c003";

/**
 * An earlier batch's PGT-A report: embryos made in March 2026 from eggs that
 * were retrieved and frozen in May 2024, so the report's retrieval date and
 * the batch's creation date differ. One euploid embryo is frozen and banked in
 * the cryo vault, so the page has two batches to choose between.
 */
const EARLIER_REPORT: EmbryoReport = {
  ...TESTED_REPORT,
  id: "00000000-0000-0000-0000-0000000e1001",
  cycleId: EARLIER_CYCLE_ID,
  reportDate: "2026-03-30",
  biopsyDate: "2026-03-16",
  retrievalDate: "2024-05-02",
  laboratory: "CooperSurgical",
  testType: "PGT-A",
  attachment: null,
  embryos: [
    embryo(1, {
      id: "00000000-0000-0000-0000-0000000e1101",
      reportId: "00000000-0000-0000-0000-0000000e1001",
      sampleId: "CS-0311-01",
      biopsyDate: "2026-03-16",
      karyotype: "46,XY",
      sex: "XY",
      result: "euploid",
      grade: "4AB",
      cnProfile: euploidProfile("XY"),
      embryoSpecimenId: "00000000-0000-0000-0000-0000000e0305",
    }),
  ],
};

/** A report whose results have not come back yet. */
const PENDING_REPORT: EmbryoReport = {
  ...TESTED_REPORT,
  reportState: "pending",
  testType: "PGT-A",
  laboratory: "CooperSurgical",
  reportDate: "2026-08-03",
  indication: "",
  attachment: null,
  embryos: [],
};

/** Results came back, and none is euploid or mosaic. */
const NO_EUPLOID_REPORT: EmbryoReport = {
  ...TESTED_REPORT,
  testType: "PGT-A",
  laboratory: "CooperSurgical",
  embryos: [
    embryo(1, {
      karyotype: "45,XX,-16",
      sex: "XX",
      result: "aneuploid",
      grade: "4BB",
      transferRecommendation: "no",
      cnProfile: withCopyNumber(euploidProfile("XX"), "16", 1),
      affectedChromosomes: ["16"],
    }),
    embryo(2, {
      karyotype: "Not reportable",
      sex: "XY",
      result: "no_result",
      grade: "3BC",
      biopsyDay: 6,
      transferRecommendation: "no",
      comments: "The biopsy did not amplify enough DNA to report.",
    }),
  ],
};

/**
 * The tested report after the lab corrected it: a re-analysis found mosaicism
 * in embryo 2, which the first version had reported as euploid.
 */
const CORRECTED_REPORT: EmbryoReport = {
  ...TESTED_REPORT,
  id: "00000000-0000-0000-0000-0000000e0002",
  version: 2,
  supersedesReportId: REPORT_ID,
  correctionReason:
    "Re-analysis of the biopsy found mosaicism on chromosome 7 in embryo 2.",
  reportDate: "2026-08-21",
};

const CORRECTED_REPORT_HISTORY: EmbryoReportHistoryEntry[] = [
  CORRECTED_REPORT,
  {
    ...TESTED_REPORT,
    reportDate: "2026-08-14",
    embryos: TESTED_REPORT.embryos.map((tested) =>
      tested.embryoNumber === 2
        ? {
            ...tested,
            karyotype: "46,XY",
            result: "euploid",
            transferRecommendation: "yes",
            mosaicism: null,
          }
        : tested,
    ),
  },
];

/** The lab withdrew the tested report. */
const WITHDRAWN_REPORT: EmbryoReport = {
  ...TESTED_REPORT,
  reportState: "retracted",
};

const EMBRYO_REPORT_FIXTURES: Record<string, EmbryoReport | null> = {
  empty: null,
  tested: TESTED_REPORT,
  pending: PENDING_REPORT,
  no_euploid: NO_EUPLOID_REPORT,
  withdrawn: WITHDRAWN_REPORT,
  corrected: CORRECTED_REPORT,
};

const EMBRYO_REPORT_HISTORY_FIXTURES: Record<
  string,
  EmbryoReportHistoryEntry[]
> = {
  corrected: CORRECTED_REPORT_HISTORY,
};

function labMetric(
  type: NonNullable<Phase["fertilityMetrics"]>[number]["type"],
  value: number,
): NonNullable<Phase["fertilityMetrics"]>[number] {
  return {
    id: `00000000-0000-0000-0000-0000000e09${String(value).padStart(2, "0")}`,
    phaseId: "00000000-0000-0000-0000-0000000e0900",
    type,
    value,
    ...AUDIT,
    source: "provider",
    verificationStatus: "verified",
    verifiedAt: STAMP,
    verifiedBy: null,
    verificationNotes: null,
  };
}

/**
 * The ICSI retrieval behind the tested report, with the lab counts the
 * Embryo testing page shows as the path from eggs to tested embryos.
 */
const TESTED_CYCLE: { cycle: Cycle; phases: Phase[] } = {
  cycle: {
    ...MOCK_CLOSED_CYCLE,
    id: TESTED_REPORT.cycleId,
    cycleType: "icsi",
    startDate: "2026-07-14",
    endDate: "2026-08-14",
    outcome: null,
  },
  phases: [
    {
      ...MOCK_CYCLE_PHASES[0],
      id: "00000000-0000-0000-0000-0000000e0900",
      cycleId: TESTED_REPORT.cycleId,
      fertilityMetrics: [
        labMetric("retrieved_oocytes", 12),
        labMetric("m2_count", 10),
        labMetric("injected_oocytes", 10),
        labMetric("fertilized_oocytes", 8),
        labMetric("embryos_day_3", 7),
        labMetric("embryos_day_5", 4),
      ],
    } as Phase,
  ],
};

const EMBRYO_CYCLE_FIXTURES: Record<string, { cycle: Cycle; phases: Phase[] }> =
  {
    tested: TESTED_CYCLE,
    corrected: TESTED_CYCLE,
  };

type VaultEmbryo = {
  id: string;
  cycleId?: string;
  collectedAt?: string;
  frozenOn?: string;
  location: { tankNumber: string; canisterNumber: string; strawNumber: string };
  dayDetails?: Specimen["dayDetails"];
  specimenUsages?: Specimen["specimenUsages"];
};

function frozenEmbryoSpecimen({
  id,
  cycleId = TESTED_REPORT.cycleId,
  collectedAt = "2026-07-28",
  frozenOn = "2026-08-02",
  location,
  dayDetails = [],
  specimenUsages = [],
}: VaultEmbryo): Specimen {
  return {
    id,
    type: "embryo",
    status: "stored",
    collectedAt,
    patientId: TESTED_REPORT.patientId,
    cycleId,
    phaseId: null,
    treatmentPlanId: null,
    treatmentPlanVersion: null,
    notes: null,
    providerId: null,
    ownership: "patient",
    ownerId: TESTED_REPORT.ownerId,
    sourceId: null,
    audience: [],
    source: "provider",
    verificationStatus: "verified",
    verifiedAt: STAMP,
    verifiedBy: null,
    verificationNotes: null,
    isDonor: false,
    ...AUDIT,
    dayDetails,
    storageLocations: [
      {
        storedFrom: frozenOn,
        storedTo: null,
        isActive: true,
        facility: "Northlake Fertility",
        storageType: "frozen",
        notes: null,
        ...location,
      },
    ],
    specimenUsages: [
      { usage: "freeze", hasDecisionAndConsent: true, date: frozenOn },
      ...specimenUsages,
    ],
  };
}

/**
 * The cryo vault behind the tested report: embryos 1 and 2 are frozen and
 * linked to the report, embryo 3 was never frozen, a fourth blastocyst from the
 * same batch was frozen without a biopsy and later transferred, and a euploid
 * embryo from an earlier batch is banked alongside them.
 */
const TESTED_VAULT: Specimen[] = [
  frozenEmbryoSpecimen({
    id: "00000000-0000-0000-0000-0000000e0301",
    location: { tankNumber: "3", canisterNumber: "B", strawNumber: "12" },
  }),
  frozenEmbryoSpecimen({
    id: "00000000-0000-0000-0000-0000000e0302",
    location: { tankNumber: "3", canisterNumber: "B", strawNumber: "13" },
  }),
  frozenEmbryoSpecimen({
    id: "00000000-0000-0000-0000-0000000e0304",
    location: { tankNumber: "3", canisterNumber: "B", strawNumber: "14" },
    specimenUsages: [
      { usage: "thaw", hasDecisionAndConsent: true, date: "2026-09-10" },
      { usage: "transfer", hasDecisionAndConsent: true, date: "2026-09-10" },
    ],
    dayDetails: [
      {
        ...observation(4, 6, {
          stage: "full_blastocyst",
          icmGrade: "B",
          teGrade: "C",
        }),
        embryoSpecimenId: "00000000-0000-0000-0000-0000000e0304",
      },
    ],
  }),
  frozenEmbryoSpecimen({
    id: "00000000-0000-0000-0000-0000000e0305",
    cycleId: "00000000-0000-0000-0000-00000000c001",
    collectedAt: "2026-03-11",
    frozenOn: "2026-03-17",
    location: { tankNumber: "3", canisterNumber: "A", strawNumber: "4" },
    specimenUsages: [
      {
        usage: "biopsy",
        hasDecisionAndConsent: true,
        date: "2026-03-16",
        embryoBiopsy: {
          id: "00000000-0000-0000-0000-0000000e0705",
          embryoSpecimenId: "00000000-0000-0000-0000-0000000e0305",
          specimenUsageId: "00000000-0000-0000-0000-0000000e0805",
          biopsyStage: "day_5",
          laserDrilling: null,
          laserPerformedOn: null,
          biopsyNeedleBrand: null,
          mediaType: null,
          mediaBrand: null,
          sentToPgta: true,
          sentToPgtm: false,
          sentToPgtsr: false,
          euploidyStatus: "euploid",
          hasMosaicism: false,
          mosaicismLevel: null,
          pgtaNoResult: null,
          pgtmScreeningStatus: null,
          pgtmInconclusiveReason: null,
          pgtmUnaffectedReason: null,
          pgtmNoResult: null,
          hasTranslocation: null,
          translocationType: null,
          translocationChromosomeDetails: null,
          hasDeletion: null,
          deletionChromosomeDetails: null,
          hasDuplication: null,
          duplicationChromosomeDetails: null,
          pgtSrNoResult: null,
          ...AUDIT,
          source: "provider",
          verificationStatus: "verified",
          verifiedAt: STAMP,
          verifiedBy: null,
          verificationNotes: null,
        },
      },
    ],
  }),
];

/**
 * While the pending report waits on the lab: two embryos from the retrieval
 * were biopsied and frozen, and their results have not come back yet.
 */
const PENDING_VAULT: Specimen[] = [1, 2].map((embryoNumber) => {
  const id = `00000000-0000-0000-0000-0000000e040${embryoNumber}`;
  return frozenEmbryoSpecimen({
    id,
    location: {
      tankNumber: "3",
      canisterNumber: "C",
      strawNumber: String(20 + embryoNumber),
    },
    dayDetails: [
      {
        ...observation(embryoNumber, embryoNumber === 1 ? 5 : 6, {
          stage: "expanded_blastocyst",
          icmGrade: "A",
          teGrade: embryoNumber === 1 ? "A" : "B",
        }),
        embryoSpecimenId: id,
      },
    ],
    specimenUsages: [
      { usage: "biopsy", hasDecisionAndConsent: true, date: "2026-08-02" },
    ],
  });
});

const EMBRYO_SPECIMEN_FIXTURES: Record<string, Specimen[]> = {
  empty: [],
  tested: TESTED_VAULT,
  corrected: TESTED_VAULT,
  withdrawn: TESTED_VAULT,
  pending: PENDING_VAULT,
  no_euploid: [],
};

function requestedFixtureKey<T>(fixtures: Record<string, T>): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("test") !== "1") return null;
    const requested = params.get("test_embryo_report");
    // Own keys only: `in` walks the prototype chain, so `toString` would hand
    // the screen a function where it expects a report.
    if (!requested || !Object.hasOwn(fixtures, requested)) return null;
    return requested;
  } catch {
    return null;
  }
}

/** Retrievals with an earlier report alongside the requested one. */
const EARLIER_REPORT_KEYS: ReadonlySet<string> = new Set([
  "tested",
  "corrected",
]);

/**
 * The requested payload for a cycle, boxed so that a fixture answering `null`
 * (the empty report, which is a real answer) is distinguishable from no
 * fixture at all. The earlier retrieval answers with its own report; every
 * other cycle answers with the requested one.
 */
export function requestedEmbryoReportFixture(cycleId?: string): {
  value: EmbryoReport | null;
} | null {
  const key = requestedFixtureKey(EMBRYO_REPORT_FIXTURES);
  if (key === null) return null;
  if (cycleId === UNREPORTED_CYCLE_ID) return { value: null };
  if (cycleId === EARLIER_CYCLE_ID) {
    return { value: EARLIER_REPORT_KEYS.has(key) ? EARLIER_REPORT : null };
  }
  return { value: EMBRYO_REPORT_FIXTURES[key] ?? null };
}

export type EmbryoTestingCycleFixture = Pick<
  Cycle,
  "id" | "cycleType" | "startDate"
>;

/**
 * The retrieval cycles behind the requested fixture. With "tested" there is
 * also an older IVF cycle with no report, so the page can offer to add one.
 */
export function requestedEmbryoTestingCyclesFixture():
  EmbryoTestingCycleFixture[] | null {
  const key = requestedFixtureKey(EMBRYO_REPORT_FIXTURES);
  if (key === null) return null;
  const tested = {
    id: TESTED_REPORT.cycleId,
    cycleType: TESTED_CYCLE.cycle.cycleType,
    startDate: TESTED_CYCLE.cycle.startDate,
  };
  if (!EARLIER_REPORT_KEYS.has(key)) return [tested];
  const earlier = {
    id: EARLIER_CYCLE_ID,
    cycleType: "ivf" as const,
    startDate: "2026-02-24",
  };
  return key === "tested"
    ? [
        tested,
        earlier,
        { id: UNREPORTED_CYCLE_ID, cycleType: "ivf", startDate: "2025-10-06" },
      ]
    : [tested, earlier];
}

/** The vault records behind the requested report's embryos. */
export function requestedEmbryoSpecimensFixture(): Specimen[] | null {
  const key = requestedFixtureKey(EMBRYO_SPECIMEN_FIXTURES);
  return key === null ? null : (EMBRYO_SPECIMEN_FIXTURES[key] ?? []);
}

/** The requested report's earlier versions, newest first. */
export function requestedEmbryoReportHistoryFixture():
  EmbryoReportHistoryEntry[] | null {
  const key = requestedFixtureKey(EMBRYO_REPORT_FIXTURES);
  return key === null ? null : (EMBRYO_REPORT_HISTORY_FIXTURES[key] ?? []);
}

/** The cycle and lab counts behind the requested report, if it has them. */
export function requestedEmbryoCycleFixture(): {
  cycle: Cycle;
  phases: Phase[];
} | null {
  const key = requestedFixtureKey(EMBRYO_REPORT_FIXTURES);
  return key === null ? null : (EMBRYO_CYCLE_FIXTURES[key] ?? null);
}
